import { IScopeClause, updateScopeClause } from "@/api";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { resolveClauseGuidance } from "@/utils/resolveClauseGuidance";
import { Close, Edit } from "@mui/icons-material";
import {
    Button,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { ClauseGuidanceAnnotation } from "./ClauseGuidanceAnnotation";
import {
    GuidanceConfirmationDialog,
    IGuidanceConfirmationDialogProps,
} from "./GuidanceConfirmationDialog";
import { RemoveGuidance } from "./RemoveGuidance";

interface IProps {
    /**
     * The scope clause to render guidance for
     */
    scopeClause: IScopeClause;
}

/**
 * Component for displaying and editing scope clause guidance
 */
export const ScopeClauseGuidance = ({ scopeClause }: IProps) => {
    const { organization } = useOrganization();
    const { open } = useDialogs();

    const [isEditing, setIsEditing] = useState(false);
    const [editingGuidance, setEditingGuidance] = useState("");

    const currentGuidance = resolveClauseGuidance(scopeClause);

    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: async (guidance: string) =>
            updateScopeClause(scopeClause.scope_id, scopeClause.id, guidance),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-scope"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-scope-tab"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-organization-scopes"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-organization-recent-scopes"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-scope-clauses", scopeClause.scope_id],
            });
            // Invalidate V2 topics with clauses query
            queryClient.invalidateQueries({
                queryKey: ["scope-topics-with-clauses", scopeClause.scope_id],
            });
        },
        onError: () => {
            enqueueSnackbar("An error occurred while updating the guidance", {
                variant: "error",
            });
        },
        onSettled: () => {
            toggleEditing();
        },
    });

    const handleSave = () => {
        const guidanceConfirmationDialogPayload: IGuidanceConfirmationDialogProps =
            {
                clause: scopeClause.clause,
                newGuidance: editingGuidance,
                organization,
                onCancel: () => {
                    toggleEditing();
                    setEditingGuidance(currentGuidance);
                },
                onConfirm: () => {
                    mutate(editingGuidance);
                },
            };

        open(GuidanceConfirmationDialog, guidanceConfirmationDialogPayload);
    };

    const toggleEditing = () => {
        if (!isEditing) {
            setEditingGuidance(currentGuidance);
        }
        setIsEditing(!isEditing);
    };

    const handleGuidanceChange = (newValue: string) => {
        setEditingGuidance(newValue);
    };

    return (
        <>
            <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
            >
                <Typography variant="body2" fontWeight={"bold"} gutterBottom>
                    Guidance
                </Typography>
                {isEditing ? (
                    <IconButton onClick={toggleEditing}>
                        <Close />
                    </IconButton>
                ) : (
                    <IconButton onClick={toggleEditing}>
                        <Edit />
                    </IconButton>
                )}
            </Stack>
            {isEditing ? (
                <OutlinedInput
                    value={editingGuidance}
                    sx={{
                        alignItems: "end",
                    }}
                    multiline
                    fullWidth
                    onChange={(e) => handleGuidanceChange(e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            <Button
                                variant="contained"
                                color="tertiary"
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "22px",
                                    minWidth: 0,
                                    maxWidth: 40,
                                    fontSize: 10,
                                    fontWeight: 500,
                                }}
                                disableElevation
                                onClick={handleSave}
                                disabled={
                                    editingGuidance === currentGuidance ||
                                    editingGuidance.trim() === ""
                                }
                            >
                                Save
                            </Button>
                        </InputAdornment>
                    }
                />
            ) : (
                <Typography
                    variant="body2"
                    gutterBottom
                    sx={{ overflowWrap: "anywhere" }}
                >
                    {currentGuidance}
                </Typography>
            )}

            <Stack>
                {/* Annotation */}
                <ClauseGuidanceAnnotation
                    scopeClause={scopeClause}
                    organization={organization}
                />

                {/* Remove Scope level guidance */}
                {scopeClause.guidance && (
                    <div>
                        <RemoveGuidance scopeClause={scopeClause} />
                    </div>
                )}
            </Stack>
        </>
    );
};
